import { useState, useCallback, useEffect } from 'react';

/**
 * Initial form data structure
 */
const getInitialFormData = () => ({
  // Annuity-specific demographic fields
  dateOfBirthAnnuity: '',
  genderAnnuity: 'Male',
  jurisdictionAnnuity: 'AL',
  
  // Life-specific demographic fields
  dateOfBirthLife: '',
  genderLife: 'Male',
  jurisdictionLife: 'AL',

  // Agent information
  firstName: '',
  lastName: '',
  agentEmail: '',
  agentPhone: '',
  agentLicenseNumber: '',

  // Proposed insured information - separate for annuity and life
  proposedInsuredAnnuityFirstName: '',
  proposedInsuredAnnuityLastName: '',
  proposedInsuredLifeFirstName: '',
  proposedInsuredLifeLastName: '',

  // Annuity-specific fields
  productTypes: ['FA'],
  premiumAnnuity: '150000',
  taxTreatment: 'Non-Qualified',
  displayValueAtYear: 5,
  selectedCarriers: [],
  features: [],
  amBestRating: ['Any'],
  surrenderChargePeriod: '',
  guaranteedPeriod: ['0'],
  minInitialRate: '0.0',
  bonusRate: false,
  bonusExc: false,
  gmbRider: false,
  indexId: '',
  solveFor: '',
  startYearOffset: 0,
  gmbStartYear: 0,
  quoteValue: '5',
  enforceRmds: false,
  productIds: [],

  // Life-specific fields
  productTabs: [],
  selectedProductTypes: [],
  healthClass: ['Preferred Plus'],
  faceAmount: 500000,
  faceAmounts: [500000],
  tobacco: false,
  productLimits: 3,
  premiumLife: '',
  premiumMode: 'Annual',
  adb_rider: false,
  wop_rider: false,
  child_rider_units: '0',
  death_benefit_option: 'Level',

  // IUL-specific premium fields
  annual_premium_iul: '',
  initial_premium_iul: '',
  to_age_iul: new Date().getFullYear() + 10,
  one_time_premium_iul: '',

  // IUL-specific face amount fields
  face_amount_type_iul: 'Minimum',
  specified_face_amount_iul: '',

  // LTC-specific fields
  monthlyBenefit: '',
  benefitPeriod: ''
});

/**
 * Custom hook to manage quote form state and validation
 * Centralizes form data management, validation, and updates
 */
const useQuoteForm = (initialData = null, onFormDataChange = null) => {
  const [formData, setFormData] = useState(initialData || getInitialFormData());
  const [formErrors, setFormErrors] = useState({});
  const [isDirty, setIsDirty] = useState(false);

  /**
   * Updates a single form field
   */
  const updateField = useCallback(
    (field, value) => {
      setFormData(prev => {
        const updated = {
          ...prev,
          [field]: value
        };

        // Defer parent notification to next tick to avoid updating during render
        if (onFormDataChange) {
          setTimeout(() => {
            onFormDataChange(updated);
          }, 0);
        }

        return updated;
      });

      setIsDirty(true);

      // Clear error for this field when user makes changes
      if (formErrors[field]) {
        setFormErrors(prev => {
          const updated = { ...prev };
          delete updated[field];
          return updated;
        });
      }
    },
    [formErrors, onFormDataChange]
  );

  /**
   * Updates multiple form fields at once
   */
  const updateFields = useCallback(
    updates => {
      setFormData(prev => {
        const updated = {
          ...prev,
          ...updates
        };

        // Defer parent notification to next tick to avoid updating during render
        if (onFormDataChange) {
          setTimeout(() => {
            onFormDataChange(updated);
          }, 0);
        }

        return updated;
      });

      setIsDirty(true);

      // Clear errors for updated fields
      const fieldsToUpdate = Object.keys(updates);
      if (fieldsToUpdate.some(field => formErrors[field])) {
        setFormErrors(prev => {
          const updated = { ...prev };
          fieldsToUpdate.forEach(field => delete updated[field]);
          return updated;
        });
      }
    },
    [formErrors, onFormDataChange]
  );

  /**
   * Resets form to initial state
   */
  const resetForm = useCallback(() => {
    setFormData(initialData || getInitialFormData());
    setFormErrors({});
    setIsDirty(false);
  }, [initialData]);

  /**
   * Sets form data directly (useful for loading saved quotes)
   */
  const setFormDataDirectly = useCallback(
    data => {
      setFormData(data);
      setIsDirty(false);
      setFormErrors({});

      // Defer parent notification to next tick to avoid updating during render
      if (onFormDataChange) {
        setTimeout(() => {
          onFormDataChange(data);
        }, 0);
      }
    },
    [onFormDataChange]
  );

  /**
   * Validates required fields based on active tab
   */
  const validateForm = useCallback(
    activeTab => {
      const errors = {};

      // Common validations
      if (!formData.dateOfBirth) {
        errors.dateOfBirth = 'Date of birth is required';
      }

      if (!formData.gender) {
        errors.gender = 'Gender is required';
      }

      if (!formData.jurisdiction) {
        errors.jurisdiction = 'State is required';
      }

      // Tab-specific validations
      if (activeTab === 'new-annuity') {
        if (!formData.premiumAnnuity || parseFloat(formData.premiumAnnuity) <= 0) {
          errors.premiumAnnuity = 'Premium must be greater than 0';
        }

        if (!formData.productTypes || formData.productTypes.length === 0) {
          errors.productTypes = 'At least one product type is required';
        }
      } else if (activeTab === 'new-life') {
        if (!formData.faceAmount || parseFloat(formData.faceAmount) <= 0) {
          errors.faceAmount = 'Face amount must be greater than 0';
        }

        if (!formData.selectedProductTypes || formData.selectedProductTypes.length === 0) {
          errors.selectedProductTypes = 'At least one product type is required';
        }
      }

      setFormErrors(errors);
      return Object.keys(errors).length === 0;
    },
    [formData]
  );

  /**
   * Clears all form errors
   */
  const clearErrors = useCallback(() => {
    setFormErrors({});
  }, []);

  /**
   * Sets a specific form error
   */
  const setFieldError = useCallback((field, message) => {
    setFormErrors(prev => ({
      ...prev,
      [field]: message
    }));
  }, []);

  return {
    formData,
    formErrors,
    isDirty,

    // Actions
    updateField,
    updateFields,
    resetForm,
    setFormDataDirectly,
    validateForm,
    clearErrors,
    setFieldError,
    setFormData
  };
};

export default useQuoteForm;
